import React, { useMemo, useState } from 'react';
import { X, User, Phone, Mail, MapPin, Calendar, FileText, MessageCircle } from 'lucide-react';
import Button from 'components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useListViewingsQuery } from '../../../store/api/propertyViewingsApi';
import { useGetConversationsQuery } from '../../../store/api/conversationsApi';
import { formatDateTime } from '../../../utils/formatDate';


const BuyerProfileModal = ({ buyer, onClose }: { buyer: any; onClose: () => void }) => {
  const [activeTab, setActiveTab] = useState('profile');

  // Fetch viewings for this buyer
  const { data: viewingsData } = useListViewingsQuery({
    page_size: 1000,
    search: buyer?.buyer_email || buyer?.buyer_name || '',
  });

  // Fetch conversations for this buyer
  const { data: conversationsList } = useGetConversationsQuery();

  // Get buyer's viewing history from API
  const buyerViewings = useMemo(() => {
    if (!viewingsData?.results || !buyer) return [];
    const buyerEmail = buyer.buyer_email || '';
    const buyerName = buyer.buyer_name || '';

    return viewingsData.results.filter((viewing) => {
      const matchesEmail = buyerEmail && viewing.buyer_email?.toLowerCase() === buyerEmail.toLowerCase();
      const matchesName = buyerName && viewing.buyer_name?.toLowerCase() === buyerName.toLowerCase();
      return matchesEmail || matchesName;
    }).map((viewing) => ({
      id: viewing.id,
      title: viewing.property_name || 'Unknown Property',
      address: viewing.property_address || '',
      viewedDate: viewing.viewing_date,
      status: viewing.status,
      feedback: viewing.notes || '',
    }));
  }, [viewingsData, buyer]);

  // Get buyer's communications from API
  const buyerCommunications = useMemo(() => {
    if (!conversationsList || !buyer) return [];
    const buyerEmail = buyer.buyer_email || '';

    return conversationsList
      .filter((conv) => {
        // Check if subject contains buyer's email or name
        return conv.subject?.toLowerCase().includes(buyerEmail.toLowerCase()) || false;
      })
      .slice(0, 10)
      .map((conv) => ({
        id: conv.id,
        type: 'email',
        date: conv.last_message_at || conv.created_at,
        subject: conv.subject,
        preview: conv.subject,
      }));
  }, [conversationsList, buyer]);

  // Build buyer data from API
  const buyerData = {
    ...buyer,
    profileImage: null,
    totalViewings: buyerViewings.length,
    propertiesViewed: buyerViewings,
    communications: buyerCommunications,
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'history', label: 'Viewing History', icon: Calendar },
    { id: 'communications', label: 'Communications', icon: MessageCircle },
    { id: 'qualification', label: 'Qualification', icon: FileText }
  ];

  // Using centralized formatDateTime utility

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { class: string; label: string }> = {
      viewed: { class: 'bg-blue-100 text-blue-800 dark:bg-blue-950/20 dark:text-blue-400', label: 'Viewed' },
      interested: { class: 'bg-green-100 text-green-800 dark:bg-green-950/20 dark:text-green-400', label: 'Interested' },
      declined: { class: 'bg-red-100 text-red-800 dark:bg-red-950/20 dark:text-red-400', label: 'Not Interested' }
    };

    const config = statusConfig[status] || { class: 'bg-gray-100 text-gray-800', label: status };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.class}`}>
        {config?.label}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-card rounded-lg border border-border w-full max-w-4xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
              <User className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">{buyerData?.buyer_name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-muted-foreground">{buyerData?.totalViewings || 0} viewings</span>
              </div>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <nav className="flex space-x-8 px-6">
            {tabs?.map((tab) => {
              const Icon = tab?.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab?.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-foreground">Contact Information</h3>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium text-foreground">{buyerData?.buyer_phone || 'N/A'}</p>
                        <p className="text-xs text-muted-foreground">Primary Phone</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium text-foreground">{buyerData?.buyer_email || 'N/A'}</p>
                        <p className="text-xs text-muted-foreground">Email Address</p>
                      </div>
                    </div>

                    {buyerData?.address && (
                      <div className="flex items-center gap-3">
                        <MapPin className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium text-foreground">{buyerData?.address}</p>
                          <p className="text-xs text-muted-foreground">Current Address</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-foreground">Viewing Statistics</h3>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium text-foreground">{buyerData?.totalViewings || 0}</p>
                        <p className="text-xs text-muted-foreground">Total Viewings</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium text-foreground">{buyerData?.communications?.length || 0}</p>
                        <p className="text-xs text-muted-foreground">Communications</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {buyerData?.notes && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">Notes</h3>
                  <div className="bg-muted rounded-lg p-4">
                    <p className="text-sm text-foreground">{buyerData?.notes}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">Viewing History</h3>

              {buyerData?.propertiesViewed && buyerData.propertiesViewed.length > 0 ? (
                <div className="space-y-4">
                  {buyerData.propertiesViewed.map((property: any, index: number) => (
                    <div key={property?.id || index} className="bg-muted rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-foreground">{property?.title}</h4>
                        <p className="text-sm text-muted-foreground">{property?.address}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Viewed on</p>
                        <p className="text-sm font-medium text-foreground">{formatDateTime(property?.viewedDate)}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getStatusBadge(property?.status)}
                      </div>
                    </div>

                    {property?.feedback && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <p className="text-sm text-muted-foreground">
                          <span className="font-medium">Feedback:</span> {property?.feedback}
                        </p>
                      </div>
                    )}
                  </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h4 className="font-medium text-foreground mb-2">No viewing history</h4>
                  <p className="text-muted-foreground text-sm">This buyer hasn't viewed any properties yet.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'communications' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">Communication History</h3>

              {buyerData?.communications && buyerData.communications.length > 0 ? (
                <div className="space-y-4">
                  {buyerData.communications.map((comm: any, index: number) => (
                    <div key={comm?.id || index} className="bg-muted rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {comm?.type === 'email' ? (
                          <Mail className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <Phone className="w-4 h-4 text-muted-foreground" />
                        )}
                        <span className="text-sm font-medium text-foreground capitalize">{comm?.type}</span>
                      </div>
                      <span className="text-sm text-muted-foreground">{formatDateTime(comm?.date)}</span>
                    </div>

                    <h4 className="font-medium text-foreground mb-1">{comm?.subject}</h4>
                    <p className="text-sm text-muted-foreground">{comm?.preview}</p>
                  </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <MessageCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h4 className="font-medium text-foreground mb-2">No communications</h4>
                  <p className="text-muted-foreground text-sm">No communication history found for this buyer.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'qualification' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-foreground">Buyer Information</h3>

              <div className="bg-muted rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-medium text-foreground">Contact Details</h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-muted-foreground">Email</p>
                        <p className="font-medium text-foreground">{buyerData?.buyer_email || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Phone</p>
                        <p className="font-medium text-foreground">{buyerData?.buyer_phone || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium text-foreground">Activity Summary</h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Viewings</p>
                        <p className="font-medium text-foreground">{buyerData?.totalViewings || 0}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Communications</p>
                        <p className="font-medium text-foreground">{buyerData?.communications?.length || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button>
            <MessageCircle className="w-4 h-4 mr-2" />
            Start Chat
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BuyerProfileModal;
